import { handleErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { showAlertSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { useAccountStorage } from '@juvare/contact-management/AppClients';
import { AccountColumns, AccountData } from '@juvare/contact-management/backend/features/accounts/shared';
import { AccountSectionComponent, useAccountSections } from '@juvare/contact-management/ui/features/accounts';
import { GetResponse } from '@juvare/platform-shared/features/storage';
import { Box, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export interface RequiredInformationAccountSectionProps {
    account: GetResponse<AccountData, AccountColumns>;
    onClose: () => void;
}

export const RequiredInformationAccountSection: React.FC<RequiredInformationAccountSectionProps> = ({ account, onClose }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const storageClient = useAccountStorage();
    const sections = useAccountSections('background-check-required-information').data;

    const handleSubmit = async (data: Partial<AccountData>, auditTags: string[] | undefined) => {
        try {
            await storageClient.post({
                action: 'partial-update',
                id: account.id,
                data,
                auditTags,
            });
        } catch (error) {
            navigate(`/accounts/${account.id}?tab=backgroundcheck`);
            onClose();

            handleErrorDialog(error);
        }
    };

    const handleSubmitSuccess = () => {
        navigate(`/accounts/${account.id}?tab=backgroundcheck`);
        onClose();

        showAlertSnackbar({
            message: t('common.dataSubmitted'),
            type: 'success',
            autoHideDuration: 2000,
            horizontalPosition: 'right',
        });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', p: 5, gap: 4 }}>
            <Typography variant='h1'>{t('backgroundCheck.labels.requiredInformationTitle')}</Typography>
            <Typography sx={{ maxWidth: 950 }}>{t('backgroundCheck.labels.requiredInformationDescription')}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {account && sections && (
                    <AccountSectionComponent
                        key={account.id}
                        section={sections[0]}
                        showSubmitButton={true}
                        data={account.data}
                        accountId={account.id}
                        onSubmit={handleSubmit}
                        onSubmitSuccess={handleSubmitSuccess}
                        showInitialErrors={true}
                    />
                )}
            </Box>
        </Box>
    );
};
